"use client";

import { usePathname, useSearchParams } from "next/navigation";
import Dialog from "./Dialog";
import { POPUP_TYPES, STORE_KEYS } from "app/constants";
import { ReactElement, useEffect, useState, useSyncExternalStore } from "react";
import { useStaticStore } from "app/hooks";
import { PopupContentLegal } from "./PopupContentLegal";
import { PopupContentCampaign } from "./PopupContentCampaign";
import Cookies from "js-cookie";
import { SiteSettingModel, TopPopupModel } from "@api/models";
import { PopupContentMission } from "./PopupContentMission";
import { PopupContentWaitFree } from "./PopupContentWaitFree";
import { PopupContentNewArticle } from "./PopupContentNewArticle";
import { GLOBALS } from "config";
import { sessionStore } from "@lib/sessionStore";

/* 
popup管理コンポーネント
閉じた後の後処理は各ポップアップで対応する？
*/

function storeViewdPopups(
  popup: POPUP_TYPES,
  userId: number,
  contentId?: number,
): Promise<Response> {
  return fetch(
    `/api/popups/storeViewedPopup?userId=${userId}&contentId=${contentId}&popup=${popup}`,
    {
      method: "POST",
    },
  );
}

export function Popup({ siteSettings }: { siteSettings: SiteSettingModel }) {
  const path = usePathname();
  const search = useSearchParams();
  const { sessionStatus } = useSyncExternalStore(
    sessionStore.subscribe,
    sessionStore.getSnapshot,
    sessionStore.getServerSnapshot,
  );
  const [popupData, setPopupData] = useState<TopPopupModel>();
  const [isPopupDataLoading, setIsPopupDataLoding] = useState<boolean>();
  const [currentPopupContent, setCurrentPopupContent] = useState<ReactElement>();
  const [currentPopupType, setCurrentPopupType] = useState<string>();
  const [preventClose, setPreventClose] = useState(false);
  const { mutate: setIsOpen } = useStaticStore(STORE_KEYS.popup);

  const [popupLoadedTime, setPopupLoadedTime] = useState<number>(0);

  useEffect(() => {
    if (sessionStatus === "loading" || isPopupDataLoading === true) {
      return;
    }
    if (isPopupDataLoading === undefined) {
      setIsPopupDataLoding(true);
      fetch("/api/popups")
        .then((res) => res.json())
        .then((res) => {
          if (res && res.topPopup) {
            //*
            setPopupData(res.topPopup as TopPopupModel);
            /*/
            //テスト用
            setPopupData(dummyResponse);
            //*/
            setIsPopupDataLoding(false);
            setPopupLoadedTime(Date.now());
          }
        })
        .catch(() => {
          //エラー時は一旦そのまま
          setIsPopupDataLoding(false);
          setPopupLoadedTime(Date.now());
        });
      return;
    }
    let popupContent, popupType;
    if (popupData) {
      const p = popupData;

      //表示すべきポップアップがあったら表示する
      let preventClose = false;
      if (p.agreementTermPopup) {
        //規約同意ポップアップはどこでも表示
        popupType = POPUP_TYPES.agreement;
        preventClose = true;
        popupContent = (
          <PopupContentLegal
            date={p.agreementTermPopup.agreementVersion || ""}
            url={p.agreementTermPopup.termUrl || ""}
            onAgree={() => {
              if (p.userId) {
                return storeViewdPopups(POPUP_TYPES.agreement, p.userId).then(() => {
                  delete p.agreementTermPopup;
                  setPopupData(structuredClone(p));
                });
              } else {
                return Promise.resolve().then(() => {
                  delete p.agreementTermPopup;
                  setPopupData(structuredClone(p));
                });
              }
            }}
            siteSettings={siteSettings}
          />
        );
      } else if (path === "/") {
        //トップページのみで表示するもの
        if (GLOBALS.HAS_MISSION && p.missionPopup && p.missionPopup.isAlreadyShown === false) {
          //ミッション
          popupType = POPUP_TYPES.mission;
          popupContent = <PopupContentMission />;
          p.missionPopup.isAlreadyShown = true;
          if (p.userId) {
            storeViewdPopups(popupType, p.userId);
          }
        } else if (p.waitFreeChargeCompletePopup?.length) {
          //待つと無料完了
          const pp = p.waitFreeChargeCompletePopup[0];
          popupType = POPUP_TYPES.waitfree;
          popupContent = <PopupContentWaitFree content={pp} />;
          p.waitFreeChargeCompletePopup.shift();
          setPopupData(structuredClone(p));
          if (p.userId) {
            storeViewdPopups(popupType, p.userId);
          }
        } else if (p.newArticlePopup?.length) {
          //新作告知
          const pp = p.newArticlePopup[0];
          popupType = POPUP_TYPES.newarticle;
          popupContent = <PopupContentNewArticle content={pp} />;
          p.newArticlePopup.shift();
          setPopupData(structuredClone(p));
          if (p.userId && pp.userPopupNewArticleId) {
            storeViewdPopups(popupType, p.userId, pp.userPopupNewArticleId);
          }
        } else if (p.campaignPopup?.length) {
          //キャンペーン
          const pp = p.campaignPopup[0];
          popupType = POPUP_TYPES.campaign;
          popupContent = (
            <PopupContentCampaign
              imageId={pp.contentPopupBannerId}
              imageUrl={pp.image.url}
              linkUrl={pp.destinationUrl}
              imageWidth={pp.image.width || 1200}
              imageHeight={pp.image.height || 1600}
              sessionStatus={sessionStatus}
            />
          );
          p.campaignPopup.shift();
          setPopupData(structuredClone(p));
          if (p.userId && pp.contentPopupBannerId) {
            storeViewdPopups(popupType, p.userId, pp.contentPopupBannerId);
          } else {
            //cookieに保存
            if (pp.contentPopupBannerId) {
              Cookies.set("popup_info" + pp.contentPopupBannerId, String(pp.contentPopupBannerId), {
                expires: 7,
              });
            }
          }
        } else if (p.recommendationPopup) {
          //レコメンド（使ってる？？）
          popupType = POPUP_TYPES.recommend;
          //popupContent = <></>;
          if (p.userId && p.recommendationPopup.articleId) {
            storeViewdPopups(popupType, p.userId, p.recommendationPopup.articleId);
          }
          delete p.recommendationPopup;
          setPopupData(structuredClone(p));
        }
      }
    }
    if (popupContent) {
      setCurrentPopupContent(popupContent);
      setPreventClose(preventClose);
      setCurrentPopupType(popupType);
      setTimeout(() => {
        //表示まで少しばかり遅延させる
        setIsOpen(true);
      }, 500);
    } else if (path === "/") {
      //トップページで表示すべきポップアップがもうない＋前回ロードから5分経過してたらポップアップ情報を再ロードする
      if (popupLoadedTime && Date.now() - popupLoadedTime > 1000 * 60 * 5) {
        setIsPopupDataLoding(undefined);
      }
    }
  }, [path, search, isPopupDataLoading, sessionStatus]);

  return (
    <Dialog
      storeKey={STORE_KEYS.popup}
      preventClose={preventClose}
      className={"mode-" + currentPopupType}
    >
      {currentPopupContent}
    </Dialog>
  );
}
